import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  applyRecompute,
  getPricing,
  previewRecompute,
  type PricingConfig,
  updatePricing,
} from '../lib/api';
import { Badge, Button, Card, ErrorBox, Label, Loading, PageHeader, TextInput } from '../components/ui';

export function PricingPage(): JSX.Element {
  const q = useQuery({ queryKey: ['pricing'], queryFn: getPricing });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox message={(q.error as Error).message} />;

  return (
    <>
      <PageHeader
        title="Pricing"
        subtitle="Edit per-country pricing inputs. After saving, run a recompute to apply to live routes."
      />
      {q.data!.items.map((p) => (
        <PricingForm key={p.id} config={p} />
      ))}
      <RecomputeBlock />
    </>
  );
}

function PricingForm({ config }: { config: PricingConfig }): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PricingConfig>(config);
  const dirty =
    draft.fuelPricePerL !== config.fuelPricePerL ||
    draft.consumptionLPer100Km !== config.consumptionLPer100Km ||
    draft.splitFactor !== config.splitFactor ||
    draft.tollPerKm !== config.tollPerKm ||
    draft.maxOverpriceRatio !== config.maxOverpriceRatio ||
    draft.reviewOverpriceRatio !== config.reviewOverpriceRatio;

  const saveMut = useMutation({
    mutationFn: () => updatePricing(config.countryCode, draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricing'] }),
  });

  const costPerKm =
    (draft.fuelPricePerL * draft.consumptionLPer100Km) / 100 * draft.splitFactor + draft.tollPerKm;

  return (
    <Card className="mb-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-brand-navy">Country: {config.countryCode}</h2>
        <Badge tone="neutral">~{costPerKm.toFixed(2)} per km</Badge>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <NumField label="Fuel price per L" value={draft.fuelPricePerL} step={1} onChange={(v) => setDraft({ ...draft, fuelPricePerL: v })} />
        <NumField label="Consumption (L/100km)" value={draft.consumptionLPer100Km} step={0.1} onChange={(v) => setDraft({ ...draft, consumptionLPer100Km: v })} />
        <NumField label="Split factor (0–1)" value={draft.splitFactor} step={0.05} onChange={(v) => setDraft({ ...draft, splitFactor: v })} />
        <NumField label="Toll per km" value={draft.tollPerKm} step={0.1} onChange={(v) => setDraft({ ...draft, tollPerKm: v })} />
        <NumField label="AA-02 max overprice ratio" value={draft.maxOverpriceRatio} step={0.1} onChange={(v) => setDraft({ ...draft, maxOverpriceRatio: v })} />
        <NumField label="AA-03 review overprice ratio" value={draft.reviewOverpriceRatio} step={0.1} onChange={(v) => setDraft({ ...draft, reviewOverpriceRatio: v })} />
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(config)}>
            Reset
          </Button>
        )}
      </div>
      {saveMut.isError && (
        <div className="mt-3">
          <ErrorBox message={(saveMut.error as Error).message} />
        </div>
      )}
    </Card>
  );
}

function NumField({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <div>
      <Label>{label}</Label>
      <TextInput
        type="number"
        step={step}
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function RecomputeBlock(): JSX.Element {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<{
    routeId: string;
    label: string;
    distanceKm: number;
    from: number;
    to: number;
  }[] | null>(null);

  const previewMut = useMutation({
    mutationFn: previewRecompute,
    onSuccess: (data) => setPreview(data.changes),
  });
  const applyMut = useMutation({
    mutationFn: applyRecompute,
    onSuccess: () => {
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['pricing'] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
    },
  });

  return (
    <Card>
      <h2 className="mb-2 text-sm font-semibold text-brand-navy">Recompute route reference costs</h2>
      <p className="mb-3 text-xs text-text-soft">
        Applies the formula to every active route. Affects new rides only — existing rides keep
        their published cost. Click <strong>Preview</strong> first to see what will change.
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>
          {previewMut.isPending ? 'Computing…' : 'Preview'}
        </Button>
        {preview && preview.length > 0 && (
          <Button variant="danger" onClick={() => applyMut.mutate()} disabled={applyMut.isPending}>
            {applyMut.isPending ? 'Applying…' : `Apply ${preview.length} updates`}
          </Button>
        )}
      </div>
      {previewMut.isError && (
        <div className="mt-3">
          <ErrorBox message={(previewMut.error as Error).message} />
        </div>
      )}
      {preview && preview.length === 0 && (
        <p className="mt-3 text-sm text-text-soft">All routes already match formula. Nothing to update.</p>
      )}
      {preview && preview.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-text-soft">
              <tr>
                <th className="px-3 py-2 font-medium">Route</th>
                <th className="px-3 py-2 font-medium">km</th>
                <th className="px-3 py-2 font-medium">Current</th>
                <th className="px-3 py-2 font-medium">→ New</th>
                <th className="px-3 py-2 font-medium">Δ</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((c) => {
                const delta = c.to - c.from;
                return (
                  <tr key={c.routeId} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">{c.label}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.distanceKm}</td>
                    <td className="px-3 py-2">{c.from}</td>
                    <td className="px-3 py-2 font-semibold">{c.to}</td>
                    <td
                      className={
                        'px-3 py-2 font-mono text-xs ' +
                        (delta < 0 ? 'text-destructive' : delta > 0 ? 'text-success' : 'text-text-soft')
                      }
                    >
                      {delta > 0 ? '+' : ''}
                      {delta}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
